import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import { cached, tracked } from '@glimmer/tracking';

import DOMPurify from 'dompurify';

import type { APIData, DateRangeInterface, Service } from 'webapp-types';
import { DataPerformanceSummaryTrend, DataPerformanceSummaryType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getRelativeTime } from 'webapp/utils/number';

import Serviced from 'webapp/models-custom/serviced';
// import MetricLimitations from 'webapp/models-custom/metric-limitations';
import type Source from 'webapp/models-custom/source';

import type { DataPerformanceSummary } from 'webapp/services/model-manager/data-performance-summaries';
import type { RSjqXHR } from 'webapp/services/request';

export interface DataPerformanceSummaryObject {
	objectId?: string; // Not passed to DataPerformanceSummaryDynamic
}

type SanitizedSummary = Omit<
	APIData.GenerativeAI.DataPerformance.Summary,
	'text' | 'suggestions' | 'created'
> & {
	text: SafeString;
	suggestions: SafeString[];
	created: string;
};

export default abstract class DataPerformanceSummaryBase extends Serviced {
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources; // Used in all extending
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;

	objectId?: string;

	protected readonly defaultTrend = DataPerformanceSummaryTrend.UNAVAILABLE;

	@tracked summariesLoading = false;
	@tracked summariesLoaded = false;

	/*
		In RSjqXHR , the 'done', 'always', and 'fail' methods correspond to old 'success', 'complete', and 'error' methods.
		As of jQuery 1.8 there's the DAFT interface (done, always, fail, and then). The then interface is the newest and it's used to chain promises together.

		summariesXHR.always(() => { next(() => { /* from '@ember/runloop' *\/ });  })
		fires before
		this.mmDataPerformanceSummariesService.getSummaries(reqBody).then or catch or finally

		Use RSjqXHR then with @ember/runloop instead to execute code after summaries request is done and digested
	 */
	@tracked summariesXHR: RSjqXHR | null = null;

	@tracked forecastLoading = false;
	@tracked forecastLoaded = false;

	@tracked summaries: APIData.GenerativeAI.DataPerformance.Summary[] = [];

	@cached
	get sampleHash() {
		// used in button component to keep data up-to-date
		return 0;
	}

	constructor(model?: DataPerformanceSummaryObject) {
		// Different for other types
		super();

		this.objectId = model?.objectId;
	}

	abstract get title(): string;
	abstract get sources(): Source[];
	abstract get trend(): DataPerformanceSummaryTrend;
	abstract get isDataIncomplete(): boolean | undefined;
	abstract get isDataEmpty(): boolean | undefined;
	protected abstract get summariesRequestBodyBase(): APIData.GenerativeAI.DataPerformance.SummariesRequest | null;
	@tracked protected abstract objectType: DataPerformanceSummaryType;
	abstract get trackingObjectType(): string;
	abstract get trackingMetricsNames(): string[];
	abstract get dateRangeFormula(): DateRangeInterface.FormulaInterface | undefined;

	get isTypeDynamic() {
		return this.objectType === DataPerformanceSummaryType.DYNAMIC;
	}

	get isNotTypeDynamic() {
		return !this.isTypeDynamic;
	}

	get isTypeBoard() {
		return this.objectType === DataPerformanceSummaryType.BOARD;
	}

	get isTypeBoardPreview() {
		return this.objectType === DataPerformanceSummaryType.BOARD_PREVIEW;
	}

	get isTypeBoardOrBoardPreview() {
		return this.isTypeBoard || this.isTypeBoardPreview;
	}

	get isTypeBlock() {
		return this.objectType === DataPerformanceSummaryType.BLOCK;
	}

	get generativeAIEnabled() {
		return this.billingService.generativeAIEnabled;
	}

	checkAndLoadAdditionalData() {
		// Different for DataPerformanceSummaryDynamic
		this.loadAdditionalData();
	}

	protected loadAdditionalData() {
		this.getSummaries();
		this.getForecast();
	}

	/* SUMMARY START */
	get canRequestSummaries() {
		// Different for DataPerformanceSummaryDynamic

		return this.generativeAIEnabled && !!this.summariesRequestBodyBase;
	}

	@cached
	get activeSummary(): APIData.GenerativeAI.DataPerformance.Summary | null {
		return this.summaries[0] || null;
	}

	@cached
	get sanitizedSummary() {
		const activeSummary = this.activeSummary;

		if (activeSummary) {
			const sanitizedSummary: SanitizedSummary = {
				text: htmlSafe(''),
				suggestions: [] as SafeString[],
				created: '',
			};

			const suggestions: string[] = activeSummary.suggestions || [];

			sanitizedSummary.text = htmlSafe(
				DOMPurify.sanitize(activeSummary.text || '', {
					ALLOWED_TAGS: ['div', 'p', 'span', 'b'],
					ALLOWED_ATTR: ['class'],
				}),
			);

			sanitizedSummary.suggestions = suggestions.map((suggestion) =>
				htmlSafe(
					DOMPurify.sanitize(suggestion, {
						ALLOWED_TAGS: [],
						ALLOWED_ATTR: [],
					}),
				),
			);

			if (activeSummary.trend) {
				sanitizedSummary.trend = activeSummary.trend;
			}

			sanitizedSummary.created = getRelativeTime(activeSummary.created);

			return sanitizedSummary;
		}

		return null;
	}

	get trendDescription(): SafeString {
		return this.mmDataPerformanceSummariesService.getTrendDescription(this.trend); // Different for DataPerformanceSummaryGoal
	}

	get summariesRequestDone() {
		return !!this.summariesXHR && !this.summariesLoading;
	}

	get summariesRequestFailed() {
		return this.summariesRequestDone && !this.summariesLoaded;
	}

	get summariesRequestSucceeded() {
		return this.summariesRequestDone && this.summariesLoaded;
	}

	get isSummariesDataEmpty() {
		return this.summariesRequestSucceeded && this.summaries.length < 1;
	}

	get noSummaryData() {
		return this.summariesRequestFailed || this.isSummariesDataEmpty;
	}

	get summariesRequestId() {
		return this.summariesXHR?.originalRequest.requestId;
	}

	protected getSummaries(refresh = false) {
		// Different for DataPerformanceSummaryBoard

		if (!this.summariesLoading && this.canRequestSummaries) {
			const reqBody = {
				...this.summariesRequestBodyBase,
				refresh,
			} as APIData.GenerativeAI.DataPerformance.SummariesRequest;

			this.summariesLoaded = false;
			this.summariesLoading = true;
			this.summaries = [];
			const promise = this.mmDataPerformanceSummariesService.getSummaries(reqBody);
			this.summariesXHR = promise.jqPromise as RSjqXHR;

			void promise
				.then((reply) => {
					// reply = window.dbx_mock['POST /d/metric-insights'];

					this.summaries = reply?.summaries || [];
					this.onSummariesChange();
					this.mmDataPerformanceSummariesService.updateLimits(reply?.limits || []);
					this.summariesLoaded = true;
				})
				.finally(() => {
					this.summariesLoading = false;
				});

			this.summariesXHR?.fail((jqXHR) => {
				if (jqXHR.status === 422) {
					// Most likely limitation hit, refresh the limits
					void this.mmDataPerformanceSummariesService.getAndUpdateLimits();
				}
			});

			if (refresh) {
				this.mmDataPerformanceSummariesService.trackEvent(
					TrackingEvent.DATA_PERFORMANCE_SUMMARY_REFRESHED,
					this as DataPerformanceSummaryBase as DataPerformanceSummary, // always one of the subclasses
					{
						choice: 'tooltip',
					},
				);
			}
		}
	}

	onSummariesChangeFn() {
		// Will be reset from the outside
	}

	onSummariesChange() {
		this.onSummariesChangeFn();
	}

	@action
	reloadDataPerformanceSummaries() {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.mmDataPerformanceSummariesService.limitReached) {
			this.getSummaries();
		}
	}

	@action
	refreshDataPerformanceSummaries() {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.mmDataPerformanceSummariesService.limitReached) {
			this.getSummaries(true);
		}
	}
	/* SUMMARY END */

	/* FORECASTING START */
	private get forecastRequestBodyBase() {
		// TODO

		return null;
	}

	get canRequestForecast() {
		// Different for DataPerformanceSummaryDynamic

		return this.billingService.forecastsEnabled && !!this.forecastRequestBodyBase;
	}

	get errorLoadingForecast() {
		return this.canRequestForecast && !this.forecastLoading && !this.forecastLoaded;
	}

	private getForecast() {
		if (!this.forecastLoading && this.canRequestForecast) {
			// const reqBody = { ...this.forecastRequestBodyBase };

			this.forecastLoaded = false;
			this.forecastLoading = true;
			void Promise.resolve(/* reqBody */)
				.then(() => {
					this.forecastLoaded = true;
				})
				// .catch(() => {})
				.finally(() => {
					this.forecastLoading = false;
				});
		}
	}

	/* FORECASTING END */
}
